import React from "react";

import { formatKst, kstNow } from "../../utils/time";
import { FAMILY_ORDER, FAMILY_META } from "../../data/strategyFamily";

/**
 * StrategyTradeTimeline — 전체 전략의 체결 활동을 "두 개의 다른 세계"로 분리해
 * 보여준다 (DAS-147 고도화).
 *
 *   ┌ 진입중 (LIVE) ─────────────────┐   실시간·미실현. 평가손익은 mark price
 *   │ ● pulse · 보유시간 · 평가손익     │   기준으로 매 폴링마다 움직인다. 종료
 *   └────────────────────────────────┘   사유가 없고 "지금 살아있는 노출".
 *
 *   ┌ 청산 완료 (SETTLED) ───────────┐   확정·실현. 일자별로 묶인 아카이브.
 *   │ 오늘 / 어제 / 날짜 · 실현손익     │   진입→청산 보유기간 + 종료 사유가
 *   └────────────────────────────────┘   붙는다.
 *
 * paper-trader 에는 전역 거래 피드가 없으므로, 상위 페이지가
 *   - 거래가 있는 전략들의 /trades (청산 완료) → normalizeTrade()
 *   - 보유 포지션이 있는 전략들의 /positions (미청산) → normalizePosition()
 * 를 병합·정렬해 props.trades 로 내려준다. 각 항목엔
 * { open, strategyId, strategyName, family } 태그가 붙는다.
 *
 * 두 세계는 카드 표면·행 레이아웃·시간 축까지 다르게 그려, 한눈에 "진입중인가
 * 끝난 거래인가" 를 구분할 수 있게 한다. 필터(유형/전략/방향)는 양쪽에 공통.
 */

const PAGE = 20;

const REASON_LABEL = {
    tp: "익절",
    sl: "손절",
    be: "본전",
    time: "시간",
    liq: "청산",
};

export default class StrategyTradeTimeline extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            familyFilter: "all", // 'all' | FAMILY_META 키 (유형별 보기)
            strategyFilter: "all",
            sideFilter: "all", // 'all' | 'long' | 'short'
            visible: PAGE,
        };
    }

    setFilter = (patch) => this.setState({ ...patch, visible: PAGE });

    // 유형을 바꾸면 하위 전략 선택은 초기화한다 (다른 유형의 전략이 남는 혼란 방지).
    setFamily = (v) =>
        this.setState({ familyFilter: v, strategyFilter: "all", visible: PAGE });

    render() {
        const { trades, loading, onRowClick } = this.props;
        const { familyFilter, strategyFilter, sideFilter, visible } = this.state;
        const all = Array.isArray(trades) ? trades : [];

        // 유형(패밀리) 칩 — 전체 분포 기준 카운트, 존재하는 유형만 노출.
        const familyCounts = {};
        for (const t of all) familyCounts[t.family] = (familyCounts[t.family] || 0) + 1;
        const presentFamilies = FAMILY_ORDER.filter((k) => familyCounts[k] > 0);

        // 전략 드롭다운 옵션 — 선택된 유형 범위 안의 전략만 (이름 기준 정렬, 중복 제거).
        const familyScoped =
            familyFilter === "all" ? all : all.filter((t) => t.family === familyFilter);
        const stratMap = new Map();
        for (const t of familyScoped) {
            if (!stratMap.has(t.strategyId)) stratMap.set(t.strategyId, t.strategyName);
        }
        const stratOptions = [...stratMap.entries()].sort((a, b) =>
            a[1].localeCompare(b[1], "ko"),
        );

        const filtered = all.filter((t) => {
            if (familyFilter !== "all" && t.family !== familyFilter) return false;
            if (strategyFilter !== "all" && t.strategyId !== strategyFilter) return false;
            if (sideFilter !== "all" && t.side !== sideFilter) return false;
            return true;
        });

        const openItems = filtered.filter((t) => t.open);
        const closedItems = filtered.filter((t) => !t.open);

        const totals = summarize(closedItems); // 실현 손익(청산 기준)
        const openPnl = openItems.reduce((s, t) => s + t.pnl, 0); // 평가손익 합

        const shownClosed = closedItems.slice(0, visible);
        const dayGroups = groupByDay(shownClosed);

        const isLoading = loading && all.length === 0;
        const noResults = !isLoading && filtered.length === 0;

        return (
            <div className="space-y-4">
                {/* ── 헤더 + 필터 (양쪽 세계 공통) ── */}
                <div className="bg-surface-card border border-surface-border rounded-lg overflow-hidden">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap px-4 pt-4">
                        <h2 className="text-base font-semibold text-content flex items-baseline gap-2">
                            체결 타임라인
                            <span className="text-xs text-content-muted font-normal">
                                진입중과 청산완료를 분리해서
                            </span>
                        </h2>
                        <span className="text-xs tabular-nums inline-flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-brand-dark">
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-1.5 w-1.5 rounded-full bg-brand animate-pulse"
                                />
                                진입중 {openItems.length}
                            </span>
                            <span className="text-surface-border-light" aria-hidden="true">
                                |
                            </span>
                            <span className="text-content-muted">청산 {closedItems.length}</span>
                        </span>
                    </div>

                    {/* 필터 — 유형 칩(그리드와 동일 어휘) + 전략 드롭다운 + 방향 */}
                    <div className="px-4 py-3 flex flex-col gap-2.5">
                        {presentFamilies.length > 1 && (
                            <div
                                className="flex items-center gap-1.5 overflow-x-auto pb-0.5 sm:flex-wrap"
                                role="group"
                                aria-label="유형(패밀리) 필터"
                            >
                                <span className="text-xs text-content-muted shrink-0 mr-0.5">
                                    유형
                                </span>
                                <FamilyChip
                                    selected={familyFilter === "all"}
                                    onClick={() => this.setFamily("all")}
                                    label="전체"
                                    count={all.length}
                                />
                                {presentFamilies.map((k) => (
                                    <FamilyChip
                                        key={k}
                                        selected={familyFilter === k}
                                        onClick={() => this.setFamily(k)}
                                        glyph={FAMILY_META[k].glyph}
                                        label={FAMILY_META[k].label}
                                        count={familyCounts[k]}
                                    />
                                ))}
                            </div>
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                            <label className="inline-flex items-center gap-1.5">
                                <span className="text-xs text-content-muted">전략</span>
                                <select
                                    value={strategyFilter}
                                    onChange={(e) =>
                                        this.setFilter({ strategyFilter: e.target.value })
                                    }
                                    aria-label="전략별 필터"
                                    className="bg-surface-card border border-surface-border text-content-secondary text-xs px-2 py-1.5 rounded min-h-11 max-w-[60vw] sm:max-w-xs focus:outline-none focus:ring-2 focus:ring-brand"
                                >
                                    <option value="all">전체 ({stratMap.size})</option>
                                    {stratOptions.map(([id, name]) => (
                                        <option key={id} value={id}>
                                            {id} · {name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <div
                                className="flex items-center gap-1 ml-auto"
                                role="group"
                                aria-label="방향 필터"
                            >
                                {[
                                    { id: "all", label: "전체" },
                                    { id: "long", label: "롱" },
                                    { id: "short", label: "숏" },
                                ].map((o) => {
                                    const sel = sideFilter === o.id;
                                    return (
                                        <button
                                            key={o.id}
                                            type="button"
                                            onClick={() => this.setFilter({ sideFilter: o.id })}
                                            aria-pressed={sel}
                                            className={`text-xs px-2.5 py-1.5 rounded border min-h-11 transition-colors ${
                                                sel
                                                    ? "bg-brand-subtle text-brand-dark border-brand/30"
                                                    : "bg-surface-card text-content-secondary border-surface-border hover:bg-surface-hover"
                                            }`}
                                        >
                                            {o.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 로딩 / 빈 상태 (공통) */}
                {isLoading && (
                    <div className="bg-surface-card border border-surface-border rounded-lg px-4 py-10 text-center text-sm text-content-secondary">
                        체결 내역을 불러오는 중…
                    </div>
                )}
                {noResults && (
                    <div className="bg-surface-card border border-surface-border rounded-lg px-4 py-10 text-center text-sm text-content-secondary">
                        조건에 맞는 내역이 없습니다.
                    </div>
                )}

                {/* ══ 세계 1 · 진입중 (LIVE) ══ */}
                {!isLoading && openItems.length > 0 && (
                    <section
                        className="bg-surface-card border border-brand/40 rounded-lg overflow-hidden"
                        aria-label="진입중 포지션"
                    >
                        <div className="flex items-center gap-2 px-4 py-3 bg-brand-subtle border-b border-brand/30">
                            <span
                                aria-hidden="true"
                                className="inline-block h-2 w-2 rounded-full bg-brand animate-pulse"
                            />
                            <span className="text-sm font-semibold text-brand-dark">
                                진입중
                            </span>
                            <span className="text-xs text-content-muted">실시간 평가</span>
                            <span className="text-xs text-content-muted tabular-nums">
                                · {openItems.length}건
                            </span>
                            <div className="ml-auto text-right leading-tight">
                                <div className="text-xs text-content-muted">평가손익 합</div>
                                <div
                                    className={`text-sm font-bold tabular-nums ${pnlClass(openPnl)}`}
                                    title="미실현 평가손익 합계 (mark price 기준)"
                                >
                                    {fmtSignedKrw(openPnl)}
                                    <span className="text-xs font-medium text-content-muted ml-1">
                                        KRW
                                    </span>
                                </div>
                            </div>
                        </div>
                        <ul className="px-2 sm:px-3 py-1">
                            {openItems.map((t, i) => (
                                <LiveTradeRow
                                    key={`open:${t.strategyId}:${t.symbol}:${t.entryTs}:${i}`}
                                    t={t}
                                    onClick={onRowClick}
                                />
                            ))}
                        </ul>
                    </section>
                )}

                {/* ══ 세계 2 · 청산 완료 (SETTLED) ══ */}
                {!isLoading && closedItems.length > 0 && (
                    <section
                        className="bg-surface-card border border-surface-border rounded-lg overflow-hidden"
                        aria-label="청산 완료 거래"
                    >
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-border-light">
                            <span
                                aria-hidden="true"
                                className="inline-block h-2 w-2 rounded-full border border-content-muted"
                            />
                            <span className="text-sm font-semibold text-content">청산 완료</span>
                            <span className="text-xs text-content-muted">실현 확정</span>
                        </div>

                        {/* 실현 손익 요약 스트립 */}
                        <div className="px-4 py-3 border-b border-surface-border-light bg-surface-card-alt flex items-center gap-4 sm:gap-6 flex-wrap">
                            <Metric label="청산" value={`${totals.count.toLocaleString()}건`} />
                            <Metric label="승 / 패" value={`${totals.wins} / ${totals.losses}`} />
                            <Metric label="승률" value={`${(totals.winRate * 100).toFixed(1)}%`} />
                            <div className="ml-auto text-right">
                                <div className="text-xs text-content-muted">실현 손익 합계</div>
                                <div
                                    className={`text-lg sm:text-xl font-bold tabular-nums leading-tight ${pnlClass(
                                        totals.pnl,
                                    )}`}
                                >
                                    {fmtSignedKrw(totals.pnl)}
                                    <span className="text-xs font-medium text-content-muted ml-1">
                                        KRW
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* 일자 그룹 */}
                        <div className="px-2 sm:px-3 pb-2">
                            {dayGroups.map((g) => (
                                <div key={g.key}>
                                    <div className="px-2 pt-3 pb-1.5 flex items-baseline gap-2">
                                        <span className="text-xs font-semibold text-content-secondary uppercase tracking-wide">
                                            {g.label}
                                        </span>
                                        <span className="text-xs text-content-muted tabular-nums">
                                            {g.items.length}건
                                        </span>
                                        <span
                                            className={`ml-auto text-xs font-semibold tabular-nums ${pnlClass(
                                                g.pnl,
                                            )}`}
                                        >
                                            {fmtSignedKrw(g.pnl)}
                                        </span>
                                    </div>
                                    <ul>
                                        {g.items.map((t, i) => (
                                            <SettledTradeRow
                                                key={`${t.strategyId}:${t.exitTs}:${t.symbol}:${i}`}
                                                t={t}
                                                onClick={onRowClick}
                                            />
                                        ))}
                                    </ul>
                                </div>
                            ))}

                            {visible < closedItems.length && (
                                <div className="px-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            this.setState((p) => ({ visible: p.visible + PAGE }))
                                        }
                                        className="w-full text-xs text-content-secondary border border-surface-border rounded-lg py-2.5 min-h-11 hover:bg-surface-hover transition-colors"
                                    >
                                        더 보기 ({Math.min(visible, closedItems.length)} /{" "}
                                        {closedItems.length})
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* 청산은 없고 진입중만 있을 때 — 청산 빈 안내 (분리감 유지) */}
                {!isLoading && closedItems.length === 0 && openItems.length > 0 && (
                    <div className="bg-surface-card border border-surface-border rounded-lg px-4 py-6 text-center text-xs text-content-muted">
                        아직 청산 완료된 거래가 없습니다 — 진입중 포지션이 청산되면 여기에 쌓입니다.
                    </div>
                )}
            </div>
        );
    }
}

/**
 * LiveTradeRow — 진입중(미청산) 1건. 시간 축은 "보유시간"(진입→지금)이며,
 * 손익은 mark price 기준 평가손익이다. brand pulse 로 살아있음을 표시.
 */
function LiveTradeRow({ t, onClick }) {
    const long = t.side === "long";
    const sideClass = long ? "bg-gain-subtle text-gain" : "bg-loss-subtle text-loss";
    const handle = () => onClick && onClick(t.strategyId);
    const entryMs = t.entryTs ? Date.parse(t.entryTs) : NaN;
    const heldMs = Number.isFinite(entryMs) ? Date.now() - entryMs : NaN;
    return (
        <li
            onClick={handle}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handle();
                }
            }}
            tabIndex={0}
            role="button"
            aria-label={`${t.strategyId} ${t.symbol} ${long ? "롱" : "숏"} 진입중, 평가손익 ${Math.round(
                t.pnl,
            ).toLocaleString()} KRW, 보유 ${fmtDuration(heldMs)}`}
            className="flex items-center gap-3 px-2 py-2.5 rounded-md border-l-2 border-brand/50 hover:bg-surface-hover focus:outline-none focus:bg-surface-hover focus:ring-1 focus:ring-inset focus:ring-brand cursor-pointer transition-colors"
        >
            {/* 보유시간 + pulse 레일 */}
            <div className="flex items-center gap-2 shrink-0 w-[68px]">
                <span
                    aria-hidden="true"
                    className="inline-block h-1.5 w-1.5 rounded-full bg-brand animate-pulse"
                />
                <span className="text-xs tabular-nums font-medium text-brand-dark leading-none">
                    {fmtDuration(heldMs)}
                </span>
            </div>

            {/* 본문 */}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded bg-brand-subtle text-brand-dark">
                        {t.strategyId}
                    </span>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${sideClass}`}>
                        {long ? "롱" : "숏"}
                    </span>
                    <span className="text-sm font-mono font-medium text-content">{t.symbol}</span>
                    <span className="inline-flex items-center gap-1 text-xs text-brand-dark bg-brand-subtle px-1.5 py-0.5 rounded">
                        <span
                            aria-hidden="true"
                            className="inline-block h-1 w-1 rounded-full bg-brand animate-pulse"
                        />
                        진입중
                    </span>
                </div>
                <div className="text-xs text-content-muted truncate mt-0.5">
                    {t.strategyName}
                    <span className="mx-1 text-surface-border">·</span>
                    진입 {formatKst(t.entryTs, "M/D HH:mm")}
                </div>
            </div>

            {/* 평가손익 */}
            <div className="text-right shrink-0">
                <div className={`text-sm font-semibold tabular-nums ${pnlClass(t.pnl)}`}>
                    {fmtSignedKrw(t.pnl)}
                </div>
                <div className="text-xs tabular-nums leading-none mt-0.5">
                    <span className={pnlClass(t.ret)}>
                        {t.ret > 0 ? "+" : ""}
                        {t.ret.toFixed(2)}%
                    </span>
                    <span className="text-content-muted ml-1">평가</span>
                </div>
            </div>
        </li>
    );
}

/**
 * SettledTradeRow — 청산 완료(확정) 1건. 시간 축은 청산 시각이며, 손익은 실현 손익.
 * 진입→청산 보유기간 + 종료 사유 배지를 붙여 "끝난 거래" 임을 분명히 한다.
 */
function SettledTradeRow({ t, onClick }) {
    const long = t.side === "long";
    const sideClass = long ? "bg-gain-subtle text-gain" : "bg-loss-subtle text-loss";
    const handle = () => onClick && onClick(t.strategyId);
    const reason = REASON_LABEL[t.reason] || t.reason || "—";
    const entryMs = t.entryTs ? Date.parse(t.entryTs) : NaN;
    const exitMs = t.exitTs ? Date.parse(t.exitTs) : NaN;
    const heldMs =
        Number.isFinite(entryMs) && Number.isFinite(exitMs) ? exitMs - entryMs : NaN;
    return (
        <li
            onClick={handle}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handle();
                }
            }}
            tabIndex={0}
            role="button"
            aria-label={`${t.strategyId} ${t.symbol} ${long ? "롱" : "숏"} 청산(${reason}), 실현손익 ${Math.round(
                t.pnl,
            ).toLocaleString()} KRW`}
            className="flex items-center gap-3 px-2 py-2.5 rounded-md border-b border-surface-border-light last:border-b-0 hover:bg-surface-hover focus:outline-none focus:bg-surface-hover focus:ring-1 focus:ring-inset focus:ring-brand cursor-pointer transition-colors"
        >
            {/* 청산 시각 + 레일 점 */}
            <div className="flex items-center gap-2 shrink-0 w-[68px]">
                <span className="text-xs tabular-nums text-content-muted w-9 text-right">
                    {formatKst(t.exitTs, "HH:mm")}
                </span>
                <span
                    aria-hidden="true"
                    className={`inline-block h-1.5 w-1.5 rounded-full ${long ? "bg-gain" : "bg-loss"}`}
                />
            </div>

            {/* 본문 */}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded bg-surface-elevated text-content-secondary">
                        {t.strategyId}
                    </span>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${sideClass}`}>
                        {long ? "롱" : "숏"}
                    </span>
                    <span className="text-sm font-mono font-medium text-content">{t.symbol}</span>
                    <span className="text-xs text-content-muted bg-surface-elevated px-1.5 py-0.5 rounded">
                        {reason}
                    </span>
                </div>
                <div className="text-xs text-content-muted truncate mt-0.5">
                    {t.strategyName}
                    {Number.isFinite(heldMs) && (
                        <>
                            <span className="mx-1 text-surface-border">·</span>
                            보유 {fmtDuration(heldMs)}
                        </>
                    )}
                    <span className="mx-1 text-surface-border">·</span>
                    <span className={`tabular-nums ${pnlClass(t.ret)}`}>
                        {t.ret > 0 ? "+" : ""}
                        {t.ret.toFixed(2)}%
                    </span>
                </div>
            </div>

            {/* 실현 손익 */}
            <div className="text-right shrink-0">
                <div className={`text-sm font-semibold tabular-nums ${pnlClass(t.pnl)}`}>
                    {fmtSignedKrw(t.pnl)}
                </div>
                <div className="text-xs text-content-muted leading-none mt-0.5">실현</div>
            </div>
        </li>
    );
}

function Metric({ label, value }) {
    return (
        <div>
            <div className="text-xs text-content-muted">{label}</div>
            <div className="text-sm font-semibold tabular-nums text-content">{value}</div>
        </div>
    );
}

// StrategyFamilyFilter(그리드)의 칩과 동일한 어휘·스타일 — 페이지 일관성 유지.
function FamilyChip({ selected, onClick, glyph, label, count }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            aria-label={`${label} 유형 ${count}건 보기`}
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border min-h-11 shrink-0 whitespace-nowrap transition-colors ${
                selected
                    ? "bg-brand-subtle text-brand-dark border-brand/30"
                    : "bg-surface-card text-content-secondary border-surface-border hover:bg-surface-hover"
            }`}
        >
            {glyph && <span aria-hidden="true">{glyph}</span>}
            {label}
            <span
                className={`tabular-nums ${selected ? "text-brand-dark" : "text-content-muted"}`}
            >
                {count}
            </span>
        </button>
    );
}

/**
 * paper-trader store.Trade(혹은 lowercase tag)를 타임라인 표시용으로 정규화한다.
 * RetPct 는 이미 퍼센트 단위(추가 ×100 금지). exitMs 가 0 이면 미청산으로 간주.
 */
export function normalizeTrade(t) {
    const entry = t.EntryTs ?? t.entry_ts ?? null;
    const exit = t.ExitTs ?? t.exit_ts ?? null;
    const exitMs = exit ? new Date(exit).getTime() : 0;
    return {
        entryTs: entry,
        exitTs: exit,
        exitMs: Number.isFinite(exitMs) ? exitMs : 0,
        symbol: t.Symbol ?? t.symbol ?? "",
        side: String(t.Side ?? t.side ?? "").toLowerCase(),
        ret: t.RetPct ?? t.ret_pct ?? 0,
        notional: t.NotionalKRW ?? t.notional_krw ?? 0,
        pnl: t.PnLKRWNet ?? t.pnl_krw_net ?? 0,
        reason: String(t.ExitReason ?? t.exit_reason ?? "").toLowerCase(),
    };
}

/**
 * paper-trader Position 을 미청산 타임라인 항목으로 정규화한다.
 * paper-trader 가 mark price 기준 미실현 PnL(UnrealizedKRW/Pct)을 이미 계산해 준다.
 * sortMs 는 진입 시각(미청산은 청산 시각이 없으므로 진입 기준 정렬).
 */
export function normalizePosition(p) {
    const entry = p.EntryTs ?? p.entry_ts ?? null;
    const ms = entry ? new Date(entry).getTime() : 0;
    return {
        entryTs: entry,
        exitTs: null,
        sortMs: Number.isFinite(ms) ? ms : 0,
        symbol: p.Symbol ?? p.symbol ?? "",
        side: String(p.Side ?? p.side ?? "").toLowerCase(),
        ret: p.UnrealizedPct ?? p.unrealized_pct ?? 0,
        notional: p.NotionalKRW ?? p.notional_krw ?? 0,
        pnl: p.UnrealizedKRW ?? p.unrealized_krw ?? 0,
        reason: "",
    };
}

function summarize(list) {
    let pnl = 0;
    let wins = 0;
    let losses = 0;
    for (const t of list) {
        pnl += t.pnl;
        if (t.pnl > 0) wins += 1;
        else if (t.pnl < 0) losses += 1;
    }
    const decided = wins + losses;
    return {
        count: list.length,
        pnl,
        wins,
        losses,
        winRate: decided > 0 ? wins / decided : 0,
    };
}

// 정렬된(최신순) 거래 배열을 KST 일자별 그룹으로 묶는다. 등장 순서 보존.
function groupByDay(list) {
    const today = kstNow().format("YYYY-MM-DD");
    const yesterday = kstNow().subtract(1, "day").format("YYYY-MM-DD");
    const groups = [];
    const index = new Map();
    for (const t of list) {
        const day = formatKst(t.exitTs, "YYYY-MM-DD");
        if (!index.has(day)) {
            const label =
                day === today
                    ? "오늘"
                    : day === yesterday
                      ? "어제"
                      : formatKst(t.exitTs, "M월 D일");
            const g = { key: day, label, items: [], pnl: 0 };
            index.set(day, g);
            groups.push(g);
        }
        const g = index.get(day);
        g.items.push(t);
        g.pnl += t.pnl;
    }
    return groups;
}

function pnlClass(v) {
    return v > 0 ? "text-gain" : v < 0 ? "text-loss" : "text-content-secondary";
}

// 부호 포함 천단위 포맷 — 색상 외 기호로도 손익 방향 보조.
function fmtSignedKrw(v) {
    const n = Math.round(v);
    const s = Math.abs(n).toLocaleString();
    return n > 0 ? `+${s}` : n < 0 ? `-${s}` : s;
}

// 보유시간 — ms 를 "45m / 3h 12m / 2d 4h" 형태로. 음수/비유한은 "—".
function fmtDuration(ms) {
    if (!Number.isFinite(ms) || ms < 0) return "—";
    const totalMin = Math.floor(ms / 60000);
    if (totalMin < 1) return "방금";
    if (totalMin < 60) return `${totalMin}m`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh ? `${d}d ${rh}h` : `${d}d`;
}
